import React from 'react';
import PropTypes from 'prop-types';
import styles from './WorkbenchSelect.css';
import forOwn from 'lodash/forOwn';

const SELECT_PLACEHOLDER = 'Select a value';

class WorkbenchMultiSelect extends React.Component {
  constructor(props) {
    super(props);
    const { multiSelectedValues } = this.props;

    this.state = {
      multiSelectedValues
    };

    this.handleApplyClick = this.handleApplyClick.bind(this);
    this.handleValueClick = this.handleValueClick.bind(this);
    this.getOptionList = this.getOptionList.bind(this);
    this.manageSelectedValues = this.manageSelectedValues.bind(this);
    this.clearSelection = this.clearSelection.bind(this);
    this.getOptionNames = this.getOptionNames.bind(this);
    this.getSelectedValues = this.getSelectedValues.bind(this);

    this.APPLY_BUTTON_NAME = 'Apply';
  }

  /**
   *  Update the state on new component props
   *  Used mainly for the 'Enter' keyDown command in the Workbench Panel
   */
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.selectedIndex === 0 && !nextProps.isOpen) {
      this.clearSelection();
    }
    if (nextProps.multiSelectedValues !== this.props.multiSelectedValues) {
      this.setState({multiSelectedValues: nextProps.multiSelectedValues});
    }
  }


  /**
   * Get the option list string names for the selected values
   */
  getOptionNames() {

    const names = [];
    const selectedValues = this.getSelectedValues();
    if (selectedValues.length === 0) {
      return SELECT_PLACEHOLDER;
    }

    selectedValues.forEach((item) => {
      names.push(' ' + item.value.name);
    });
    return names.toString();
  }

  /**
   * Get a list of the values marked as selected
   */
  getSelectedValues() {

    const selectedValues = [];
    forOwn(this.state.multiSelectedValues, (selectedValue) => {
      if (selectedValue.selected) {
        selectedValues.push(selectedValue);
      }
    });
    return selectedValues;
  }


  /**
   * Get the list of DOM elements for the multi select options
   */
  getOptionList(options, selectedIndex) {

    return options.map((option, index) => {

      const isApply = option.name === this.APPLY_BUTTON_NAME;
      const isHighlighted = selectedIndex === index;

      let style = styles.listItem;
      let isSelected = false;

      if (option._id) {
        isSelected = this.state.multiSelectedValues[option._id].selected;
      }
      // Get the correct css class for the option, selected, highlighted, or the apply button
      const listItemClass = () => {

        if (isHighlighted) {
          style = isApply ? styles.selectedApplyButton : styles.selectedListItem;
        }
        else if (isSelected) {
          style = styles.selectedMultiListItem;
        }
        else if (isApply) {
          style = styles.applyButton;
        }

        return style;
      };

      return (
        <div
          key={index}
          className={listItemClass()}
          onClick={(e) => {
            if (isApply) {
              this.handleApplyClick(e);
            } else {
              this.handleValueClick(e, index);
            }
          }}
        >
          {option.name}
        </div>
      );
    });
  }

  /**
   *  Handle the click event for the 'Apply' button
   */
  handleApplyClick(e) {

    const { onSelectedIndexChanged } = this.props;

    if(e) {
      e.stopPropagation();
    }

    const selectedIndices = [];
    forOwn(this.state.multiSelectedValues, (value) => {
      if (value.selected) {
        selectedIndices.push(value.index);
        onSelectedIndexChanged(value.index, false);
      }
    });

    // If no values were selected, close the menu and select the (Select) option
    if (selectedIndices.length === 0) {
      onSelectedIndexChanged(0, false);
    }
  }

  /**
   * Handle the event for a click on a value option
   */
  handleValueClick(e, index) {

    const { onSelectedIndexChanged } = this.props;

    if(e) {
      e.stopPropagation();
    }

    // If the index is 0, we know it is the (Select) option, clear the selection
    if (index !== 0) {
      this.manageSelectedValues(index);
      return onSelectedIndexChanged(index, true);
    }
    return this.clearSelection();
  }

  /**
   * Set all the of the multi selected values to selected = false
   * Used for selecting the (Select) option
   */
  clearSelection() {
    forOwn(this.state.multiSelectedValues, (selectedValue) => {
      selectedValue.selected = false;
    });

    this.setState({
      multiSelectedValues: this.state.multiSelectedValues
    });
  }

  /**
   * Manage the selected value state.
   * Mainly used for clicking on a multi select value
   */
  manageSelectedValues(selectedIndex) {

    forOwn(this.state.multiSelectedValues, (selectedValue) => {
      if (selectedValue.index === selectedIndex) {
        selectedValue.selected = !selectedValue.selected;
      }
    });

    this.setState({
      multiSelectedValues: this.state.multiSelectedValues
    });
  }

  /**
   * Render the multi select component, taking updated props from the workbench panel component state changes.
   */
  render() {

    const {values, isOpen, selectedIndex} = this.props;

    // Prepend 'empty' value to the list
    const options = [{
      name: SELECT_PLACEHOLDER,
      _id: undefined
    }].concat(values);

    // Append the 'Apply' button option to the list
    options.push({
      name: this.APPLY_BUTTON_NAME,
      _id: undefined
    });

    // Create the first null selected item of <Select>
    const currentValue = (
      <span className={this.getOptionNames() === SELECT_PLACEHOLDER ? styles.select : styles.value}>
        {this.getOptionNames()}
      </span>
    );
    const valueContainer = <div>{currentValue}</div>;

    // Create the option list
    const optionList = this.getOptionList(options, selectedIndex);

    return (
      <div tabIndex="0" className={styles.workbenchSelect}>
        {isOpen ? optionList : valueContainer}
      </div>
    );
  }
}

WorkbenchMultiSelect.propTypes = {
  values: PropTypes.array,
  selectedIndex: PropTypes.number,
  isFocused: PropTypes.bool,
  isOpen: PropTypes.bool,
  onSelectedIndexChanged: PropTypes.func,
  fields: PropTypes.array,
  multiSelectedValues: PropTypes.object
};

WorkbenchMultiSelect.defaultProps = {
  values: [],
  selectedIndex: 0,
  isFocused: false,
  isOpen: false,
  onSelectedIndexChanged: () => undefined,
  multiSelectedValues: {}
};

export default WorkbenchMultiSelect;
